import { uuidv4 } from '@firebase/util';
import React, { useState } from 'react';

import { FileAttachement, PartialFileAttachement } from '../../../pages/mindmap/types';
import { useMindmap } from '../../../pages/mindmap/use-mindmap';
import { useUnmount } from '../../../utils/useUnmount';
import { PanelMenu, PanelMenuValue } from '../../ui/panel-menu';
import { FileAttachementChip } from './file-attachement-chip';

interface FileContextMenuProps {
  attachement?: FileAttachement;
  nodeId?: string;
  creatingMode?: boolean;
  onCreateClick?: (attachement: PartialFileAttachement | undefined) => void;
}

export const FileContextMenu = ({ attachement, nodeId, creatingMode, onCreateClick }: FileContextMenuProps) => {
  const {
    manipulation: {
      updateAttachement,
      deleteAttachement,
      setShadowNodeAttachementsWidth,
      calculateMinAttachementDimensions,
    },
    contextMenu: { setActiveMenu, activeMenu },
    data: { nodeMap },
  } = useMindmap();

  const [attachementChanges, setAttachementChanges] = useState<PartialFileAttachement | undefined>();

  const onDeleteClick = () => {
    if (nodeId && attachement) {
      deleteAttachement(nodeId, attachement.id);
      setActiveMenu(undefined);
    }
  };

  const onChange = () => {
    if (nodeId) {
      // update shadow text element width because text breaks can change if width changes
      const node = nodeMap[nodeId];
      if (node) {
        const newNode = {
          ...node,
          attachements: creatingMode
            ? [...(node.attachements || []), createUpdatedAttachement()]
            : node.attachements?.map((a) => (a.id == attachement?.id ? { ...a, ...createUpdatedAttachement() } : a)),
        };
        const { attachementMinWidth } = calculateMinAttachementDimensions(newNode);
        setShadowNodeAttachementsWidth(attachementMinWidth);
      }
    }
  };

  const onChangeTitle: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setAttachementChanges({
      ...attachement,
      ...attachementChanges,
      name: e.target.value,
    });
    onChange();
  };

  const onSetAsPreviewClick = () => {
    onChange();
    if (attachement && nodeId) {
      setAttachementChanges({ ...attachementChanges, isPreview: !attachement.isPreview });
      const newAttachement: FileAttachement = {
        ...attachement,
        isPreview: !attachement.isPreview,
      };
      updateAttachement(nodeId, newAttachement);
      // update menu in activeMenu
      setActiveMenu({ ...activeMenu, type: 'edit-file-attachement', nodeId, attachement: newAttachement });
    }
  };

  const createUpdatedAttachement = (): FileAttachement => {
    const tempChipRenderer = document.getElementById(`temp-file-chip-renderer-${nodeId}`);
    const width = tempChipRenderer
      ? parseFloat(parseFloat(window.getComputedStyle(tempChipRenderer).width).toPrecision(12))
      : 0;

    return {
      id: uuidv4(),
      isPreview: false,
      name: '',
      type: 'file',
      filePreviewHeight: 120,
      url: '',
      ...attachement,
      ...attachementChanges,
      width: width || attachement?.width || 0,
    };
  };

  const applyAttachementChanges = () => {
    if (attachement && nodeId) {
      updateAttachement(nodeId, createUpdatedAttachement());
      setAttachementChanges(undefined);
    }
  };

  useUnmount(() => {
    if (attachementChanges) applyAttachementChanges();
  });

  const panelMenu: PanelMenuValue = [
    {
      label: 'Titel',
      icon: 'pi pi-fw ri-palette-line',
      type: 'input-text',
      value: attachementChanges?.name || attachement?.name,
      onChange: onChangeTitle,
      autoselect: true,
    },
    {
      separator: true,
    },
    ...(creatingMode
      ? [
          {
            label: 'Speichern',
            icon: 'pi pi-fw pi-save',
            onClick: () => (onCreateClick ? onCreateClick(createUpdatedAttachement()) : () => {}),
          },
        ]
      : [
          {
            label: 'Löschen',
            icon: 'pi pi-fw ri-delete-bin-2-line',
            onClick: onDeleteClick,
          },
          {
            label: attachement?.isPreview ? 'Vorschau löschen' : 'Vorschau setzen',
            icon: 'pi pi-fw ri-bookmark-3-line',
            onClick: onSetAsPreviewClick,
          },
        ]),
  ];

  return (
    <>
      <PanelMenu value={panelMenu} />
      <div style={{ position: 'absolute', width: '1000vw', visibility: 'hidden' }}>
        <FileAttachementChip
          id={`temp-file-chip-renderer-${nodeId}`}
          attachement={{
            id: '',
            isPreview: false,
            name: '',
            type: 'file',
            filePreviewHeight: 120,
            url: '',
            width: 0,
            ...attachement,
            ...attachementChanges,
          }}
          selected={false}
        />
      </div>
    </>
  );
};
